import React, { useCallback, useEffect, useRef, useState } from "react";
import "../Accounts/Accounts.scss";
import uploadImage from "../../assets/images/upload-dummy-image.png";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../utils/auth";
import useApi from "../../hook/useAPI";
import { toast } from "react-toastify";
import { USER_MANAGEMENT } from "../../utils/const";
import { Formik } from "formik";
import * as Yup from "yup";
import { deepEqual, getProfileImage, validateImage } from "../../utils/common";
import useUserDetailsRefresh from "../../hook/useUserDetailsRefresh";
import Subscription from "../../components/Subscription/Subscription";
import UserNoticesPanel from "../../components/UserNotices/UserNoticesPanel";
import { openStripeCustomerPortal } from "../../utils/stripePortal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function Accounts() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("UserRole");
  const { putData, postData, postFileData } = useApi();
  const [uploadPhoto, setUploadPhoto] = useState(null);
  const [errorImage, setErrorImage] = useState({});
  const [profileFileName, setProfileFileName] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
  });

  const { userDetails, activePlan, refreshUserDetails } = useUserDetailsRefresh();
  const userDetailsRequestedRef = useRef(false);
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().min(2).max(50).required("First name required"),
    lastName: Yup.string().min(2).max(50).required("Last name required"),
    phoneNumber: Yup.string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    dateOfBirth: Yup.date()
      .max(new Date())
      .required("date of birth is required"),
    gender: Yup.string().oneOf(["male", "female", "other"]).required(),
  });

  const hydrateAccountForm = useCallback((user) => {
    if (!user) {
      return;
    }
    setUserData({
      firstName: user.firstname,
      lastName: user.lastname,
      email: user.email,
      phoneNumber: user.phonenumber,
      dateOfBirth: user.dateofbirth,
      gender: user.gender,
    });
    setProfileFileName(user.profileFileName);
    getProfileImage(user.profileFileName, setUploadPhoto);
  }, []);

  const fetchUserDetails = useCallback(async () => {
    if (!token) {
      return;
    }
    const users = await refreshUserDetails();
    if (users?.user) {
      hydrateAccountForm(users.user);
    }
  }, [hydrateAccountForm, refreshUserDetails, token]);

  useEffect(() => {
    if (userDetails) {
      hydrateAccountForm(userDetails);
      return;
    }
    if (!userDetailsRequestedRef.current) {
      userDetailsRequestedRef.current = true;
      refreshUserDetails();
    }
  }, [hydrateAccountForm, refreshUserDetails, userDetails]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("billing") === "return" || params.get("paymentStatus")) {
      fetchUserDetails();
    }
    if (location.hash === "#plans") {
      document.getElementById("account-plans")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [fetchUserDetails, location.hash, location.pathname, location.search]);

  const handleUpdateSubmit = async (formValues) => {
    if (!deepEqual(formValues, userData) || isUpdated) {
      try {
        const payload = {
          firstname: formValues?.firstName,
          lastname: formValues?.lastName,
          phonenumber: formValues?.phoneNumber,
          dateofbirth: formValues?.dateOfBirth,
          gender: formValues?.gender,
        };
        if (profileFileName !== "") {
          payload.profileFileName = profileFileName;
        }
        const response = await putData(`${USER_MANAGEMENT.UPDATE_USER}`, payload);

        if (response.status === 200) {
          fetchUserDetails();
          setIsUpdated(false);
          toast.success("User data updated successfully!");
        } else {
          toast.error("Failed to update user data.");
        }
      } catch (error) {
        console.error("Error updating user data:", error);
      }
    }
  };

  const handleManageBilling = async () => {
    try {
      await openStripeCustomerPortal(postData, "/main/account", activePlan);
    } catch (error) {
      toast.error("Unable to open billing portal.");
      document.getElementById("account-plans")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    let imageErrors = {};
    if (file) {
      imageErrors = validateImage(file);
      if (Object.keys(imageErrors).length === 0) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadPhoto(reader.result);
        };
        reader.readAsDataURL(file);
        const formData = new FormData();
        formData.append("profilePic", file);
        try {
          const response = await postFileData(
            `${USER_MANAGEMENT.UPLOAD_PROFILE_PICTURE}`,
            formData,
          );
          if (response?.fileName) {
            setProfileFileName(response?.fileName);
            setIsUpdated(true);
          }
        } catch (err) {
          console.error("Error while uploading profile");
        }
      }
    }
    setErrorImage({ ...imageErrors });
  };

  return (
    <div className="dashboard account-settings-page">
        {userRole !== "1" && (
          <UserNoticesPanel
            activePlan={activePlan}
            onUpgrade={() => document.getElementById("account-plans")?.scrollIntoView({ behavior: "smooth" })}
            onManageBilling={handleManageBilling}
          />
        )}

        <section className="account-profile-panel card">
          <div className="card-body">
            <div className="account-panel-heading">
              <div className="account-panel-heading-row">
                <div className="account-panel-heading-copy">
                  <h4>Profile</h4>
                  <p>Update your personal details and profile photo.</p>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary account-logout-btn"
                  onClick={() => logout(navigate)}
                >
                  Log out
                </button>
              </div>
            </div>
            <div className="account-profile-layout">
              <div className="account-profile-photo">
                <div className="upload-img-btn">
                      <label htmlFor="uploadProfile">
                        <img
                          src={uploadPhoto ? uploadPhoto : uploadImage}
                          className="img-fluid upload_photo"
                          alt=""
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = uploadImage;
                          }}
                        />
                        <input
                          type="file"
                          id="uploadProfile"
                          accept="image/*"
                          hidden
                          onChange={handleImageChange}
                        />
                      </label>
                      {!uploadPhoto && <h5>Upload Photo</h5>}
                      {errorImage.image && (
                        <div className="upload-photo-error-message">
                          {errorImage.image}
                        </div>
                      )}
                </div>
              </div>
              <div className="account-profile-fields">
                    <Formik
                      initialValues={userData}
                      enableReinitialize
                      validationSchema={validationSchema}
                      onSubmit={handleUpdateSubmit}
                    >
                      {({
                        values,
                        touched,
                        errors,
                        isValid,
                        dirty,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                      }) => (
                        <form className="account-profile-form">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="form_field_wrapper">
                                <label>First Name</label>
                                <input
                                  type="text"
                                  value={values?.firstName}
                                  className="form-control"
                                  name="firstName"
                                  placeholder="Enter your first name"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {errors.firstName && touched?.firstName && (
                                  <div className="error-message error-message-height" id="feedback">
                                    {errors.firstName}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form_field_wrapper">
                                <label>Last Name</label>
                                <input
                                  type="text"
                                  value={values.lastName}
                                  name="lastName"
                                  className="form-control"
                                  placeholder="Enter your last name"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {errors.lastName && touched?.lastName && (
                                  <div className="error-message error-message-height" id="feedback">
                                    {errors.lastName}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form_field_wrapper">
                                <label>Your email</label>
                                <input
                                  type="text"
                                  value={values.email}
                                  name="email"
                                  className="form-control"
                                  disabled
                                />
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form_field_wrapper">
                                <label>Phone Number</label>
                                <input
                                  type="text"
                                  value={values.phoneNumber}
                                  name="phoneNumber"
                                  className="form-control"
                                  placeholder="Enter your phone number"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {errors.phoneNumber && touched?.phoneNumber && (
                                  <div className="error-message error-message-height" id="feedback">
                                    {errors.phoneNumber}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form_field_wrapper">
                                <label>Date of Birth</label>
                                <DatePicker
                                  selected={
                                    values.dateOfBirth
                                      ? new Date(values.dateOfBirth)
                                      : null
                                  }
                                  onChange={(date) => setFieldValue("dateOfBirth", date)}
                                  maxDate={new Date()}
                                  dateFormat="dd-MM-yyyy"
                                  showYearDropdown
                                  showMonthDropdown
                                  scrollableYearDropdown
                                  yearDropdownItemNumber={100}
                                  placeholderText="DD/MM/YYYY"
                                  className="form-control"
                                  showIcon
                                />
                                {errors.dateOfBirth && touched?.dateOfBirth && (
                                  <div className="error-message error-message-height" id="feedback">
                                    {errors.dateOfBirth}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form_field_wrapper account-gender-field">
                                <label>Gender</label>
                                <select
                                  className="form-select"
                                  value={values.gender}
                                  name="gender"
                                  aria-label="Default select example"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">Select gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                  <option value="other">Other</option>
                                </select>
                                {errors.gender && touched?.gender && (
                                  <div className="error-message error-message-height" id="feedback">
                                    {errors.gender}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="account-form-actions">
                            <button
                              type="submit"
                              className="btn btn-primary account-save-btn"
                              onClick={handleSubmit}
                              disabled={!(isValid && dirty) && !isUpdated ? "disabled" : ""}
                            >
                              Update Changes
                            </button>
                          </div>
                        </form>
                      )}
                    </Formik>
              </div>
            </div>
          </div>
        </section>

        {userRole !== "1" && (
          <section id="account-plans" className="account-subscription-section card">
            <div className="card-body">
              <Subscription
                embedded
                returnPath="/main/account"
              />
            </div>
          </section>
        )}
    </div>
  );
}

export default Accounts;
